import { useState, useEffect, useCallback } from "react";
import { STORAGE_KEYS } from "@/config/storageKeys";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { X, Sparkles, Clock, Shield } from "lucide-react";
import { Button } from "@/components/ui/button";
import { trackExitIntentShown, trackExitIntentClick } from "@/lib/analytics";

const ExitIntentPopup = ({ page = "default" }) => {
  const { t } = useTranslation();
  const [visible, setVisible] = useState(false);
  const [triggered, setTriggered] = useState(false);

  const handleMouseLeave = useCallback((e) => {
    if (triggered) return;
    if (e.clientY <= 0) {
      setVisible(true);
      setTriggered(true);
      trackExitIntentShown();
    }
  }, [triggered]);

  useEffect(() => {
    // Ne pas afficher si déjà vu récemment (24h)
    const lastSeen = localStorage.getItem(STORAGE_KEYS.EXIT_INTENT_SEEN);
    if (lastSeen && Date.now() - parseInt(lastSeen) < 86400000) return;

    const timer = setTimeout(() => {
      document.addEventListener("mouseleave", handleMouseLeave);
    }, 8000); // Attendre 8s avant d'activer

    return () => {
      clearTimeout(timer);
      document.removeEventListener("mouseleave", handleMouseLeave);
    };
  }, [handleMouseLeave]);

  const handleClose = () => {
    setVisible(false);
    localStorage.setItem(STORAGE_KEYS.EXIT_INTENT_SEEN, Date.now().toString());
  };

  const handleCTA = () => {
    trackExitIntentClick();
    localStorage.setItem(STORAGE_KEYS.EXIT_INTENT_SEEN, Date.now().toString());
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4"
      onClick={handleClose}>
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" />
      <div
        className="relative glass rounded-3xl p-8 max-w-md w-full border border-primary/30 shadow-2xl"
        onClick={e => e.stopPropagation()}
        style={{ animation: "slideUp 0.3s ease-out" }}
      >
        {/* Close */}
        <button onClick={handleClose}
          className="absolute top-4 right-4 text-slate-400 hover:text-white transition-colors">
          <X className="w-5 h-5" />
        </button>

        {/* Badge */}
        <div className="flex justify-center mb-4">
          <span className="inline-flex items-center gap-2 bg-primary/20 text-primary px-4 py-1.5 rounded-full text-sm font-medium border border-primary/30">
            <Sparkles className="w-4 h-4" />
            {t('exitIntent.badge')}
          </span>
        </div>

        {/* Titre */}
        <h2 className="font-heading text-2xl font-bold text-white text-center mb-2">
          {t('exitIntent.title')}
        </h2>
        <p className="text-slate-400 text-center mb-6">
          {t('exitIntent.subtitle')}
        </p>

        {/* Points clés */}
        <div className="space-y-3 mb-6">
          {[
            { icon: Clock, key: "point1" },
            { icon: Shield, key: "point2" },
            { icon: Sparkles, key: "point3" },
          ].map(({ icon: Icon, key }) => (
            <div key={key} className="flex items-center gap-3 text-sm text-slate-300">
              <div className="w-6 h-6 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0">
                <Icon className="w-3.5 h-3.5 text-primary" />
              </div>
              {t(`exitIntent.${key}`)}
            </div>
          ))}
        </div>

        {/* CTA */}
        <Link to="/register" onClick={handleCTA}>
          <Button size="lg" className="w-full btn-glow bg-primary-strong hover:bg-primary-strong/90 text-base font-semibold">
            {t('exitIntent.cta')}
          </Button>
        </Link>

        <p className="text-center text-slate-400 text-xs mt-3">
          {t('exitIntent.noCreditCard')}
        </p>
      </div>

    </div>
  );
};

export default ExitIntentPopup;
